import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as companyApi from '../../services/company-api.js';
import { listGoogleAccounts, type GoogleAccount } from '../../services/google-account-api.js';

@customElement('cp-company-drive-widget')
export class CpCompanyDriveWidget extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) companyId = 0;
  @property({ type: String }) driveFolderId: string | null = null;
  @property({ type: String }) driveAccount: string | null = null;
  @property({ type: String }) driveFolderName: string | null = null;

  @state() private _showForm = false;
  @state() private _formFolderId = '';
  @state() private _formAccount = '';
  @state() private _formFolderName = '';
  @state() private _formEntireDrive = false;
  @state() private _saving = false;
  @state() private _error = '';
  @state() private _googleAccounts: GoogleAccount[] = [];

  override connectedCallback(): void {
    super.connectedCallback();
    this._loadGoogleAccounts();
  }

  private async _loadGoogleAccounts(): Promise<void> {
    try {
      this._googleAccounts = await listGoogleAccounts();
    } catch {
      this._googleAccounts = [];
    }
  }

  private _startEdit(): void {
    this._showForm = true;
    this._formEntireDrive = this.driveFolderId === 'root';
    this._formFolderId = this.driveFolderId === 'root' ? '' : (this.driveFolderId ?? '');
    this._formAccount = this.driveAccount ?? (this._googleAccounts[0]?.account_alias || '');
    this._formFolderName = this.driveFolderName ?? '';
    this._error = '';
  }

  private _cancel(): void {
    this._showForm = false;
    this._error = '';
  }

  private async _save(): Promise<void> {
    if (!this._formEntireDrive && !this._formFolderId.trim()) {
      this._error = 'Ordner-ID ist erforderlich';
      return;
    }
    const folderId = this._formEntireDrive ? 'root' : this._formFolderId.trim();
    this._saving = true;
    this._error = '';
    try {
      await companyApi.setCompanyDriveFolder(this.companyId, {
        drive_folder_id: folderId,
        drive_account: this._formAccount,
        drive_folder_name: this._formEntireDrive ? 'Gesamter Drive' : (this._formFolderName.trim() || null),
      });
      this._showForm = false;
      this.dispatchEvent(new CustomEvent('company-updated', { bubbles: true, composed: true }));
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern';
    } finally {
      this._saving = false;
    }
  }

  private async _remove(): Promise<void> {
    this._saving = true;
    this._error = '';
    try {
      await companyApi.setCompanyDriveFolder(this.companyId, {
        drive_folder_id: null,
        drive_account: null,
        drive_folder_name: null,
      });
      this._showForm = false;
      this.dispatchEvent(new CustomEvent('company-updated', { bubbles: true, composed: true }));
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Entfernen';
    } finally {
      this._saving = false;
    }
  }

  override render() {
    return html`
      <div class="cp-drive">
        ${this._error ? html`
          <p class="cp-drive__error">${this._error}</p>
        ` : nothing}

        ${!this.driveFolderId && !this._showForm ? html`
          <p class="cp-drive__empty">Kein Drive-Ordner zugewiesen</p>
          <div class="cp-drive__add">
            <button class="cp-drive__action-link" @click=${this._startEdit}>+ Verknuepfen</button>
          </div>
        ` : nothing}

        ${this.driveFolderId && !this._showForm ? html`
          <div class="cp-drive__info">
            <div class="cp-drive__info-row">
              <span class="cp-drive__label">Ordner</span>
              <a
                class="cp-drive__folder-link"
                href="https://drive.google.com/drive/folders/${this.driveFolderId}"
                target="_blank"
                rel="noopener"
              >${this.driveFolderName ?? this.driveFolderId}</a>
            </div>
            <div class="cp-drive__info-row">
              <span class="cp-drive__label">Account</span>
              <span class="cp-drive__badge">${this.driveAccount ?? 'private'}</span>
            </div>
            <div class="cp-drive__actions">
              <button class="cp-drive__action-link" @click=${this._startEdit}>Bearbeiten</button>
              <span class="cp-drive__action-sep">&middot;</span>
              <button class="cp-drive__action-link cp-drive__action-link--danger" @click=${this._remove}>Entfernen</button>
            </div>
          </div>
        ` : nothing}

        ${this._showForm ? html`
          <div class="cp-drive__form">
            <label class="cp-drive__checkbox-row">
              <input
                type="checkbox"
                .checked=${this._formEntireDrive}
                @change=${(e: Event) => { this._formEntireDrive = (e.target as HTMLInputElement).checked; }}
              />
              <span>Gesamter Drive</span>
            </label>
            ${!this._formEntireDrive ? html`
              <input
                type="text"
                class="cp-drive__input"
                placeholder="Ordner-ID (aus Google Drive URL)"
                .value=${this._formFolderId}
                @input=${(e: Event) => { this._formFolderId = (e.target as HTMLInputElement).value; }}
              />
            ` : nothing}
            <select
              class="cp-drive__select"
              .value=${this._formAccount}
              @change=${(e: Event) => { this._formAccount = (e.target as HTMLSelectElement).value; }}
            >
              ${this._googleAccounts.map((a) => html`
                <option value=${a.account_alias} ?selected=${this._formAccount === a.account_alias}>${a.account_alias} (${a.google_email})</option>
              `)}
            </select>
            ${!this._formEntireDrive ? html`
              <input
                type="text"
                class="cp-drive__input"
                placeholder="Anzeigename (optional)"
                .value=${this._formFolderName}
                @input=${(e: Event) => { this._formFolderName = (e.target as HTMLInputElement).value; }}
              />
            ` : nothing}
            <div class="cp-drive__form-actions">
              <button
                class="cp-drive__btn-save"
                @click=${this._save}
                ?disabled=${this._saving}
              >${this._saving ? 'Speichert...' : 'Speichern'}</button>
              <button
                class="cp-drive__btn-cancel"
                @click=${this._cancel}
              >Abbrechen</button>
            </div>
          </div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-company-drive-widget': CpCompanyDriveWidget;
  }
}
